// src/constants/enums/movementTypes.js

/**
 * ERP Movement Type Governance
 * Centralized Movement Definitions
 * 
 * PURPOSE:
 * - Standardize all ERP movement events
 * - Remove duplicate movement naming
 * - Enable:
 *   Ledger Engine
 *   Replay Engine
 *   Audit Engine
 *   Movement Validation
 *   Custody Tracking
 * 
 * IMPORTANT:
 * Every inventory/asset movement
 * must use standardized movement types.
 */

export const MOVEMENT_TYPES = {
    INITIAL_STOCK: 'INITIAL_STOCK',
    ISSUE: 'ISSUE',

    /**
     * Asset/material transfer
     * Example:
     * Technician → Site
     * Store → Warehouse
     */
    TRANSFER: 'TRANSFER',

    /**
     * Return initiated
     * Example:
     * Technician → Store return request
     */
    RETURN: 'RETURN',

    /**
     * Return/transfer physically received
     */
    RECEIVE: 'RECEIVE',

    /**
     * Goods received from vendor
     * Procurement inward flow
     */
    GRN: 'GRN',

    /**
     * Asset/material marked defective
     */
    DEFECTIVE: 'DEFECTIVE',

    /**
     * Asset/material sent for repair
     */
    REPAIR_OUT: 'REPAIR_OUT',

    /**
     * Asset/material returned from repair
     */
    REPAIR_IN: 'REPAIR_IN',

    /**
     * Asset/material scrapped/disposed
     */
    SCRAP: 'SCRAP',

    /**
     * Movement reversal/correction
     * Replay-safe rollback support
     */
    REVERSAL: 'REVERSAL',

    /**
     * Manual admin adjustment
     * Should be highly restricted
     */
    ADJUSTMENT: 'ADJUSTMENT',

};


/**
 * Movement direction governance
 * Useful for ledger engines
 */
export const MOVEMENT_DIRECTIONS = {

    INWARD: 'INWARD',
    OUTWARD: 'OUTWARD',
    INTERNAL: 'INTERNAL',
    REVERSAL: 'REVERSAL',

};


/**
 * Mapping movement → direction
 * Centralized ledger interpretation
 */
export const MOVEMENT_TYPE_DIRECTIONS = {
    [MOVEMENT_TYPES.INITIAL_STOCK]:
        MOVEMENT_DIRECTIONS.INWARD,
    [MOVEMENT_TYPES.ISSUE]:
        MOVEMENT_DIRECTIONS.OUTWARD,

    [MOVEMENT_TYPES.TRANSFER]:
        MOVEMENT_DIRECTIONS.INTERNAL,

    [MOVEMENT_TYPES.RETURN]:
        MOVEMENT_DIRECTIONS.INWARD,

    [MOVEMENT_TYPES.RECEIVE]:
        MOVEMENT_DIRECTIONS.INWARD,

    [MOVEMENT_TYPES.GRN]:
        MOVEMENT_DIRECTIONS.INWARD,

    [MOVEMENT_TYPES.DEFECTIVE]:
        MOVEMENT_DIRECTIONS.INTERNAL,

    [MOVEMENT_TYPES.REPAIR_OUT]:
        MOVEMENT_DIRECTIONS.OUTWARD,

    [MOVEMENT_TYPES.REPAIR_IN]:
        MOVEMENT_DIRECTIONS.INWARD,

    [MOVEMENT_TYPES.SCRAP]:
        MOVEMENT_DIRECTIONS.OUTWARD,

    [MOVEMENT_TYPES.REVERSAL]:
        MOVEMENT_DIRECTIONS.REVERSAL,

    [MOVEMENT_TYPES.ADJUSTMENT]:
        MOVEMENT_DIRECTIONS.INTERNAL,

};


/**
 * Validation helper
 */
export const isValidMovementType = (type) => {

    return Object.values(MOVEMENT_TYPES).includes(type);

};


/**
 * UI-safe display labels
 */
export const MOVEMENT_TYPE_LABELS = {
    [MOVEMENT_TYPES.INITIAL_STOCK]: 'Initial Stock',
    [MOVEMENT_TYPES.ISSUE]: 'Issue',
    [MOVEMENT_TYPES.TRANSFER]: 'Transfer',
    [MOVEMENT_TYPES.RETURN]: 'Return',
    [MOVEMENT_TYPES.RECEIVE]: 'Receive',
    [MOVEMENT_TYPES.GRN]: 'GRN',
    [MOVEMENT_TYPES.DEFECTIVE]: 'Defective',
    [MOVEMENT_TYPES.REPAIR_OUT]: 'Repair Out',
    [MOVEMENT_TYPES.REPAIR_IN]: 'Repair In',
    [MOVEMENT_TYPES.SCRAP]: 'Scrap',
    [MOVEMENT_TYPES.REVERSAL]: 'Reversal',
    [MOVEMENT_TYPES.ADJUSTMENT]: 'Adjustment',

};